import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { PhotoAsk } from '@goaltree/backend/convex/lib/habitDots'
import {
  PHOTO_CAPTION_MAX,
  VIDEO_MAX_BYTES,
  VIDEO_MAX_SECONDS,
} from '@goaltree/backend/convex/lib/habitPhotos'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { Drawer, DrawerContent, DrawerTitle } from '@goaltree/ui/components/drawer'
import { type StickerKind, isEdited } from '@goaltree/ui/lib/photo-effects'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import {
  Camera,
  Check,
  CirclePlus,
  ImageIcon,
  Loader2,
  Play,
  RefreshCw,
  Share2,
  Trash2,
  Video,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react'
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import type { CellMark } from '@/components/habit-dots'
import { habitColor } from '@/components/inbox-habits'
import {
  PhotoEditorCanvas,
  PhotoEditorControls,
  useHabitStickers,
  usePhotoEdit,
} from '@/components/photo-editor'
import { useAddToStory } from '@/components/stories'
import { useCloseOnOverlayTap } from '@/hooks/use-close-on-overlay-tap'
import { useIsMobile } from '@/hooks/use-mobile'
import { useCategoryFill } from '@/lib/category-color'
import { logicalDayStr } from '@/lib/day'
import { bakePhoto } from '@/lib/photo-bake'
import { openShareCard } from '@/lib/share-card'

export type HabitPhoto = FunctionReturnType<typeof api.habitPhotos.forHabits>[number]
export type PhotoMap = Map<string, HabitPhoto>

export const PHOTO_IMAGE_MIN_PX = 12
export const PLAY_GLYPH_MIN_PX = 14

/** A habit's photos by day (a year back); skipped unless `on`. */
export function useHabitPhotos(habitId: string | undefined, on: boolean): PhotoMap | null {
  const list = useQuery(
    api.habitPhotos.forHabits,
    on && habitId ? { habitIds: [habitId as Id<'nodes'>] } : 'skip',
  )
  return useMemo(() => (list ? new Map(list.map((p) => [p.date, p])) : null), [list])
}

/** The day's still: the photo or a video's poster, else the clip's first frame. */
export function DayStill({ photo, className }: { photo: HabitPhoto; className?: string }) {
  if (photo.url)
    return <img src={photo.url} alt="" draggable={false} loading="lazy" className={className} />
  return (
    <video
      src={photo.kind === 'video' ? `${photo.videoUrl}#t=0.1` : undefined}
      muted
      playsInline
      preload="metadata"
      className={cn('pointer-events-none', className)}
    />
  )
}

export function PlayGlyph({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'pointer-events-none absolute inset-0 grid place-items-center text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.7)]',
        className,
      )}
    >
      <Play className="size-[45%] max-h-5 max-w-5 fill-current" strokeWidth={0} />
    </span>
  )
}

const HOVER_PREVIEW_PX = 120
const canHover = () =>
  typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches

/** A video day's cell; on hover devices, resting the pointer on it plays the clip above it. */
function VideoCell({ photo, size }: { photo: HabitPhoto & { kind: 'video' }; size: number }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const hide = useCallback(() => {
    clearTimeout(timer.current)
    setAt(null)
  }, [])
  useEffect(() => {
    if (!at) return
    window.addEventListener('scroll', hide, true)
    return () => window.removeEventListener('scroll', hide, true)
  }, [at, hide])
  useEffect(() => () => clearTimeout(timer.current), [])
  const half = HOVER_PREVIEW_PX / 2 + 8
  return (
    <span
      className="relative block size-full"
      onMouseEnter={(e) => {
        if (!canHover()) return
        const r = e.currentTarget.getBoundingClientRect()
        clearTimeout(timer.current)
        timer.current = setTimeout(() => setAt({ x: r.left + r.width / 2, y: r.top }), 150)
      }}
      onMouseLeave={hide}
    >
      <DayStill photo={photo} className="block size-full rounded-full object-cover" />
      {size >= PLAY_GLYPH_MIN_PX && <PlayGlyph />}
      {at &&
        createPortal(
          <video
            src={photo.videoUrl}
            poster={photo.url ?? undefined}
            autoPlay
            muted
            loop
            playsInline
            className="pointer-events-none fixed z-[80] -translate-x-1/2 -translate-y-full rounded-xl bg-muted object-cover shadow-lg"
            style={{
              width: HOVER_PREVIEW_PX,
              height: HOVER_PREVIEW_PX,
              left: Math.min(Math.max(at.x, half), window.innerWidth - half),
              top: Math.max(at.y - 6, HOVER_PREVIEW_PX + 8),
            }}
          />,
          document.body,
        )}
    </span>
  )
}

/** A cell showing the day's photo or video poster, round-cropped; null below the readable size. */
export function photoMark(photo: HabitPhoto | undefined, size: number): CellMark | null {
  if (!photo || size < PHOTO_IMAGE_MIN_PX) return null
  return {
    // Round like the photo, so today's outline hugs it instead of boxing it.
    style: {
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      filter: 'none',
      borderRadius: '9999px',
    },
    children:
      photo.kind === 'video' ? (
        <VideoCell photo={photo} size={size} />
      ) : (
        <span className="relative block size-full">
          <DayStill photo={photo} className="block size-full rounded-full object-cover" />
        </span>
      ),
  }
}

const PHOTO_MAX_PX = 1280
const PHOTO_QUALITY = 0.88

export async function downscale(file: File, max = PHOTO_MAX_PX): Promise<Blob> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = reject
      el.src = url
    })
    const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(img.naturalWidth * k)
    canvas.height = Math.round(img.naturalHeight * k)
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height)
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('encode'))),
        'image/jpeg',
        PHOTO_QUALITY,
      ),
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}

const POSTER_PX = 512

const once = (el: HTMLVideoElement, ok: string, ms: number) =>
  new Promise<boolean>((resolve) => {
    const done = (v: boolean) => {
      clearTimeout(timer)
      el.removeEventListener(ok, yes)
      el.removeEventListener('error', no)
      resolve(v)
    }
    const yes = () => done(true)
    const no = () => done(false)
    const timer = setTimeout(no, ms)
    el.addEventListener(ok, yes)
    el.addEventListener('error', no)
  })

/** A picked clip's length, and its first frame as a centre-cropped square JPEG (null if the browser won't draw it). */
async function readClip(file: File): Promise<{ duration: number; poster: Blob | null }> {
  const url = URL.createObjectURL(file)
  const el = document.createElement('video')
  el.muted = true
  el.playsInline = true
  el.preload = 'auto'
  try {
    const meta = once(el, 'loadedmetadata', 8000)
    el.src = url
    if (!(await meta)) throw new Error('video')
    const duration = el.duration
    if (el.readyState < 2) {
      const data = once(el, 'loadeddata', 3000)
      // iOS Safari only decodes a frame after a seek.
      el.currentTime = 0.001
      await data
    }
    const w = el.videoWidth
    const h = el.videoHeight
    if (el.readyState < 2 || !w || !h) return { duration, poster: null }
    const side = Math.min(w, h)
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = POSTER_PX
    canvas
      .getContext('2d')!
      .drawImage(el, (w - side) / 2, (h - side) / 2, side, side, 0, 0, POSTER_PX, POSTER_PX)
    const poster = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    )
    return { duration, poster }
  } finally {
    el.removeAttribute('src')
    el.load()
    URL.revokeObjectURL(url)
  }
}

export type CaptureKind = 'photo' | 'video'

function useSaveCapture(habitId: Id<'nodes'>) {
  const { t } = useTranslation()
  const uploadUrl = useMutation(api.habitPhotos.uploadUrl)
  const save = useAction(api.habitPhotosNode.save)
  const saveVideo = useMutation(api.habitPhotos.saveVideo)
  const addToStory = useAddToStory()
  const saved = (key: string, date: string) =>
    toast.success(
      t(key),
      date === logicalDayStr(Date.now())
        ? {
            action: {
              label: t('stories.addToStory'),
              onClick: () => void addToStory(habitId, date),
            },
            duration: 8000,
          }
        : undefined,
    )
  const upload = async (blob: Blob, type: string) => {
    const res = await fetch(await uploadUrl({}), {
      method: 'POST',
      headers: { 'Content-Type': type },
      body: blob,
    })
    if (!res.ok) throw new Error('upload')
    return ((await res.json()) as { storageId: Id<'_storage'> }).storageId
  }
  // A baked edit arrives as a Blob already at upload size.
  const photo = async (file: File | Blob, date: string, caption: string) => {
    try {
      const blob = file instanceof File ? await downscale(file).catch(() => file) : file
      const storageId = await upload(blob, blob.type || 'image/jpeg')
      await save({ habitId, date, storageId, caption: caption.trim() || undefined })
      saved('dotPhoto.saved', date)
      return true
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code
      toast.error(t(code === 'tooLarge' ? 'dotPhoto.errors.tooLarge' : 'dotPhoto.errors.badImage'))
      return false
    }
  }
  const video = async (file: File, date: string, caption: string) => {
    const fail = (key: string) => {
      toast.error(t(key, { seconds: VIDEO_MAX_SECONDS, mb: VIDEO_MAX_BYTES / 1024 / 1024 }))
      return false
    }
    if (file.size > VIDEO_MAX_BYTES) return fail('dotPhoto.errors.videoTooLarge')
    const clip = await readClip(file).catch(() => null)
    if (!clip) return fail('dotPhoto.errors.badVideo')
    // Camera clips stopped at the limit can run a few frames over.
    if (Number.isFinite(clip.duration) && clip.duration > VIDEO_MAX_SECONDS + 0.25)
      return fail('dotPhoto.errors.videoTooLong')
    try {
      const storageId = await upload(file, file.type.startsWith('video/') ? file.type : 'video/mp4')
      const posterStorageId = clip.poster ? await upload(clip.poster, 'image/jpeg') : undefined
      const out = await saveVideo({
        habitId,
        date,
        storageId,
        posterStorageId,
        caption: caption.trim() || undefined,
      })
      if (!out.ok)
        return fail(
          out.code === 'tooLarge' ? 'dotPhoto.errors.videoTooLarge' : 'dotPhoto.errors.badVideo',
        )
      saved('dotPhoto.videoSaved', date)
      return true
    } catch {
      return fail('dotPhoto.errors.badVideo')
    }
  }
  return (kind: CaptureKind, file: File | Blob, date: string, caption: string) =>
    kind === 'video' && file instanceof File
      ? video(file, date, caption)
      : photo(file, date, caption)
}

const ASKS: PhotoAsk[] = ['always', 'sometimes', 'never']

function Sheet({
  open,
  onOpenChange,
  habitColorVar,
  children,
  title,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  habitColorVar: string
  children: ReactNode
  title: string
}) {
  const isMobile = useIsMobile()
  useCloseOnOverlayTap(open && isMobile, onOpenChange)
  const Title = isMobile ? DrawerTitle : DialogTitle
  const body = (
    <div
      // Padding follows the drawer/dialog choice, not the sm breakpoint: tablets
      // between sm and the mobile cutoff get the drawer and need its padding.
      className={cn(
        'flex flex-col gap-3',
        isMobile
          ? 'max-h-[88dvh] overflow-y-auto overscroll-contain px-5 pt-3 pb-[max(env(safe-area-inset-bottom),24px)]'
          : 'px-1 pb-1',
      )}
      style={{ '--habit': habitColorVar } as CSSProperties}
    >
      <Title className="sr-only">{title}</Title>
      {children}
    </div>
  )
  // Portal clicks (buttons, Close, the backdrop) still bubble through React to whatever
  // rendered this sheet, e.g. the habit sheet's History block, which opens the stats page.
  return (
    <div className="contents" onClick={(e) => e.stopPropagation()}>
      {isMobile ? (
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="mx-auto w-full max-w-lg overflow-hidden data-[vaul-drawer-direction=bottom]:rounded-t-3xl">
            {body}
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-sm">{body}</DialogContent>
        </Dialog>
      )}
    </div>
  )
}

/** "Add a photo to today's dot?": Camera, Library, caption, Not now. */
export function PhotoCheckinSheet({
  habitId,
  title,
  category,
  date,
  ask,
  checkedIn = true,
  onClose,
  discover,
}: {
  habitId: Id<'nodes'>
  title: string
  category?: string
  date: string
  ask: PhotoAsk
  checkedIn?: boolean
  onClose: () => void
  // Habit isn't Photo style yet: a save switches it. 'tip' also shows the intro line.
  discover?: 'tip' | 'link'
}) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const dismiss = useMutation(api.habitPhotos.dismissAsk)
  const setAsk = useMutation(api.habitPhotos.setPhotoAsk)
  const save = useSaveCapture(habitId)
  const setStyle = useMutation(api.habitDots.setDotStyle)
  const [caption, setCaption] = useState('')
  const [busy, setBusy] = useState(false)
  const [mode, setMode] = useState(ask)
  const [kind, setKind] = useState<CaptureKind>('photo')
  const [draft, setDraft] = useState<File | null>(null)
  const [edit, setEdit, resetEdit] = usePhotoEdit()
  const [selected, setSelected] = useState<StickerKind | null>(null)
  const specs = useHabitStickers(habitId, date)
  const isMobile = useIsMobile()
  const camera = useRef<HTMLInputElement>(null)
  const library = useRef<HTMLInputElement>(null)
  const done = useRef(false)

  const pick = async (f: File | undefined) => {
    if (!f) return
    const k = f.type.startsWith('video/') ? 'video' : kind
    if (k === 'photo') {
      resetEdit()
      setSelected(null)
      setDraft(f)
      return
    }
    await commit(k, f)
  }
  const savePhoto = async () => {
    if (!draft) return
    if (!isEdited(edit)) return commit('photo', draft)
    setBusy(true)
    const baked = await bakePhoto(draft, edit, specs, {
      max: PHOTO_MAX_PX,
      quality: PHOTO_QUALITY,
      aspect: 1,
    }).catch(() => null)
    setBusy(false)
    if (!baked) {
      toast.error(t('photoEdit.bakeFailed'))
      return
    }
    await commit('photo', baked)
  }
  const commit = async (k: CaptureKind, f: File | Blob) => {
    setBusy(true)
    const ok = await save(k, f, date, caption)
    setBusy(false)
    if (ok) {
      done.current = true
      onClose()
      if (discover)
        await setStyle({ habitId, style: { kind: 'photo' } })
          .then(() => setAsk({ habitId, ask: 'sometimes' }))
          .catch(() => {})
    }
  }
  const close = (open: boolean) => {
    if (open || busy) return
    if (!done.current && checkedIn && !discover) void dismiss({ habitId }).catch(() => {})
    onClose()
  }
  const input = (ref: typeof camera, capture?: 'environment' | 'user') => (
    <input
      ref={ref}
      type="file"
      accept={kind === 'video' ? 'video/*' : 'image/*'}
      capture={capture}
      className="hidden"
      onChange={(e) => {
        const f = e.target.files?.[0]
        e.target.value = ''
        void pick(f)
      }}
    />
  )
  const name = title.split('///')[0].trim()
  return (
    <Sheet
      open
      onOpenChange={close}
      habitColorVar={fill(habitColor(category))}
      title={t('dotPhoto.title')}
    >
      <div className="flex items-center gap-2.5">
        {discover === 'tip' && (
          <p className="text-[13px] font-bold text-foreground/80">{t('dotPhoto.discoverIntro')}</p>
        )}
        <span className="grid size-7 shrink-0 place-items-center rounded-full bg-[var(--habit)] text-background">
          <Check className="size-4" strokeWidth={3} />
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-[15px] font-extrabold">
            {checkedIn ? t('dotPhoto.doneTitle', { title: name }) : name}
          </span>
          <span className="text-xs text-muted-foreground">
            {t(kind === 'video' ? 'dotPhoto.videoTitle' : 'dotPhoto.title')}
          </span>
        </div>
      </div>
      {draft ? (
        <>
          <PhotoEditorCanvas
            source={draft}
            aspect={1}
            specs={specs}
            edit={edit}
            onEdit={setEdit}
            selected={selected}
            onSelect={setSelected}
            className="mx-auto max-w-[min(100%,36dvh)] rounded-2xl"
          />
          <PhotoEditorControls
            source={draft}
            specs={specs}
            edit={edit}
            onEdit={setEdit}
            selected={selected}
            onSelect={setSelected}
            disabled={busy}
          />
        </>
      ) : (
        <>
          <div className="inline-flex self-start rounded-xl border border-border/60 p-0.5">
            {(['photo', 'video'] as const).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={kind === k}
                disabled={busy}
                onClick={() => setKind(k)}
                className={cn(
                  'flex cursor-pointer items-center gap-1.5 rounded-[10px] px-3 py-1 text-[12.5px] font-bold transition-colors',
                  kind === k
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {k === 'video' ? (
                  <Video className="size-3.5" />
                ) : (
                  <ImageIcon className="size-3.5" />
                )}
                {t(k === 'video' ? 'dotPhoto.kindVideo' : 'dotPhoto.kindPhoto')}
              </button>
            ))}
          </div>
          {kind === 'video' && (
            <p className="-mt-1 text-[11.5px] text-muted-foreground">
              {t('dotPhoto.videoHint', {
                seconds: VIDEO_MAX_SECONDS,
                mb: VIDEO_MAX_BYTES / 1024 / 1024,
              })}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => camera.current?.click()}
              className="flex h-12 cursor-pointer items-center justify-center gap-2 rounded-[14px] bg-foreground text-sm font-extrabold text-background transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="size-[18px] animate-spin" />
              ) : kind === 'video' ? (
                <Video className="size-[18px]" />
              ) : (
                <Camera className="size-[18px]" />
              )}
              {t(kind === 'video' ? 'dotPhoto.record' : 'dotPhoto.camera')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => library.current?.click()}
              className="flex h-12 cursor-pointer items-center justify-center gap-2 rounded-[14px] border border-border text-sm font-bold transition-colors hover:bg-muted disabled:opacity-60"
            >
              <ImageIcon className="size-[18px]" />
              {t('dotPhoto.library')}
            </button>
          </div>
          {input(camera, kind === 'video' ? 'user' : 'environment')}
          {input(library)}
        </>
      )}
      <label htmlFor="dot-photo-caption" className="text-xs font-bold text-foreground/80">
        {t('dotPhoto.captionLabel')}
      </label>
      <input
        id="dot-photo-caption"
        value={caption}
        maxLength={PHOTO_CAPTION_MAX}
        onChange={(e) => setCaption(e.target.value)}
        placeholder={t('dotPhoto.captionPlaceholder')}
        className="h-[42px] rounded-xl border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      />
      {draft ? (
        <div
          className={cn(
            'grid grid-cols-2 gap-2',
            isMobile && 'sticky -bottom-6 z-10 -mx-5 bg-background px-5 pt-2 pb-6',
          )}
        >
          <button
            type="button"
            disabled={busy}
            onClick={() => setDraft(null)}
            className="flex h-12 cursor-pointer items-center justify-center rounded-[14px] border border-border text-sm font-bold transition-colors hover:bg-muted disabled:opacity-60"
          >
            {t('photoEdit.back')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void savePhoto()}
            className="flex h-12 cursor-pointer items-center justify-center gap-2 rounded-[14px] bg-foreground text-sm font-extrabold text-background transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="size-[18px] animate-spin" />
            ) : (
              <Check className="size-[18px]" />
            )}
            {t('photoEdit.save')}
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => close(false)}
            className="cursor-pointer py-2 text-[13px] font-semibold text-muted-foreground hover:text-foreground"
          >
            {t('dotPhoto.notNow')}
          </button>
          <div className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <span>{t('dotPhoto.askLabel')}</span>
            <div className="inline-flex rounded-lg border border-border/60 p-0.5">
              {ASKS.map((a) => (
                <button
                  key={a}
                  type="button"
                  aria-pressed={mode === a}
                  onClick={() => {
                    setMode(a)
                    void setAsk({ habitId, ask: a })
                  }}
                  className={cn(
                    'cursor-pointer rounded-md px-2 py-0.5 text-[11px] font-semibold transition-colors',
                    mode === a
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`dotPhoto.ask.${a}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Sheet>
  )
}

/** One day's photo, large, with time, caption, Delete and Share to Instagram. */
export function PhotoViewer({
  photo,
  habitId,
  title,
  category,
  onReplace,
  onClose,
}: {
  photo: HabitPhoto
  habitId: Id<'nodes'>
  title: string
  category?: string
  // Set when the day is still inside the window where a capture can be added.
  onReplace?: () => void
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const fill = useCategoryFill()
  const remove = useMutation(api.habitPhotos.remove)
  const addToStory = useAddToStory()
  const mine = useQuery(api.stories.mine)
  const isToday = photo.date === logicalDayStr(Date.now())
  const onStory = !!mine?.some((s) => s.habitId === habitId && s.date === photo.date)
  const when = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(photo.createdAt))
  const name = title.split('///')[0].trim()
  return (
    <Sheet
      open
      onOpenChange={(o) => !o && onClose()}
      habitColorVar={fill(habitColor(category))}
      title={name}
    >
      <div className="relative mx-auto aspect-square w-full max-w-xs overflow-hidden rounded-2xl bg-muted">
        {photo.kind === 'video' ? (
          <ClipPlayer src={photo.videoUrl} poster={photo.url} label={photo.caption ?? name} />
        ) : (
          <img src={photo.url} alt={photo.caption ?? name} className="size-full object-cover" />
        )}
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-extrabold">{when}</span>
        <span className="text-[12.5px] text-muted-foreground">{name}</span>
        {photo.caption && <span className="text-[13.5px]">“{photo.caption}”</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => {
            onClose()
            openShareCard({
              type: 'streak',
              ref: habitId,
              habit: habitId,
              photos: true,
            })
          }}
          className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-foreground text-sm font-extrabold text-background hover:opacity-90"
        >
          <Share2 className="size-4" />
          {t('dotPhoto.shareStory')}
        </button>
        <button
          type="button"
          onClick={() => {
            void remove({ habitId, date: photo.date })
              .then(() => toast.success(t('dotPhoto.deleted')))
              .catch(() => toast.error(t('dotPhoto.errors.badImage')))
            onClose()
          }}
          className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-border text-sm font-bold text-destructive hover:bg-muted"
        >
          <Trash2 className="size-4" />
          {t('dotPhoto.delete')}
        </button>
      </div>
      {isToday && mine && (
        <button
          type="button"
          disabled={onStory}
          onClick={() => void addToStory(habitId, photo.date)}
          className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-border text-sm font-bold hover:bg-muted disabled:cursor-default disabled:text-muted-foreground disabled:hover:bg-transparent"
        >
          {onStory ? <Check className="size-4" /> : <CirclePlus className="size-4" />}
          {t(onStory ? 'stories.onStory' : 'stories.addToStory')}
        </button>
      )}
      {onReplace && (
        <button
          type="button"
          onClick={onReplace}
          className="flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-border text-sm font-bold hover:bg-muted"
        >
          <RefreshCw className="size-4" />
          {t('dotPhoto.replace')}
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        className="flex cursor-pointer items-center justify-center gap-1 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        <X className="size-3.5" />
        {t('dotPhoto.close')}
      </button>
    </Sheet>
  )
}

/** Muted autoplay on loop; a tap turns the sound on and off. */
function ClipPlayer({ src, poster, label }: { src: string; poster: string | null; label: string }) {
  const { t } = useTranslation()
  const [muted, setMuted] = useState(true)
  const ref = useRef<HTMLVideoElement>(null)
  const Icon = muted ? VolumeX : Volume2
  return (
    <button
      type="button"
      aria-label={t(muted ? 'dotPhoto.soundOn' : 'dotPhoto.soundOff')}
      onClick={() => {
        setMuted((m) => !m)
        void ref.current?.play().catch(() => {})
      }}
      className="relative block size-full cursor-pointer"
    >
      <video
        ref={ref}
        src={src}
        poster={poster ?? undefined}
        aria-label={label}
        autoPlay
        loop
        muted={muted}
        playsInline
        className="size-full object-cover"
      />
      <span className="absolute bottom-2 right-2 grid size-7 place-items-center rounded-full bg-black/50 text-white">
        <Icon className="size-4" />
      </span>
    </button>
  )
}

type Pending = { kind: 'view'; photo: HabitPhoto } | { kind: 'add'; date: string } | null

/**
 * Press handling for a photo habit's cells: a photo opens the viewer, a done
 * day without one (today or a few days back) offers to add one.
 */
export function usePhotoPress(
  habit: { id: string; title: string; category?: string; photoAsk: PhotoAsk } | null,
  photos: PhotoMap | null,
  canAdd: (ds: string) => boolean,
) {
  const [pending, setPending] = useState<Pending>(null)
  const onDay = useCallback(
    (ds: string) => {
      if (!habit || !photos) return false
      const photo = photos.get(ds)
      if (photo) {
        setPending({ kind: 'view', photo })
        return true
      }
      if (canAdd(ds)) {
        setPending({ kind: 'add', date: ds })
        return true
      }
      return false
    },
    [habit, photos, canAdd],
  )
  const node =
    habit && pending ? (
      pending.kind === 'view' ? (
        <PhotoViewer
          photo={pending.photo}
          habitId={habit.id as Id<'nodes'>}
          title={habit.title}
          category={habit.category}
          onReplace={
            canAdd(pending.photo.date)
              ? () => setPending({ kind: 'add', date: pending.photo.date })
              : undefined
          }
          onClose={() => setPending(null)}
        />
      ) : (
        <PhotoCheckinSheet
          habitId={habit.id as Id<'nodes'>}
          title={habit.title}
          category={habit.category}
          date={pending.date}
          ask={habit.photoAsk}
          checkedIn={false}
          onClose={() => setPending(null)}
        />
      )
    ) : null
  return { onDay, node }
}

/**
 * Mounted once: when a photo-style habit flips to done during this session,
 * asks the server whether to offer a photo (it keeps the "sometimes" rule).
 */
export function PhotoCheckinHost() {
  const habits = useQuery(api.nodes.getHabits, {})
  const ask = useMutation(api.habitPhotos.askAfterCheckIn)
  const seen = useRef<Map<string, boolean> | null>(null)
  const claimTip = useMutation(api.habitPhotos.claimPhotoTip)
  const prefs = useQuery(api.preferences.getMyPreferences)
  const tipSeen = prefs ? prefs.seenTips.includes('photoDiscover') : true
  const [req, setReq] = useState<{
    habitId: Id<'nodes'>
    title: string
    category: string
    date: string
    ask: PhotoAsk
    discover?: 'tip'
  } | null>(null)
  useEffect(() => {
    if (!habits) return
    const today = logicalDayStr(Date.now())
    const next = new Map<string, boolean>()
    const tip = !tipSeen && !habits.some((h) => h.dot?.kind === 'photo')
    for (const h of habits) {
      const photo = h.dot?.kind === 'photo'
      if (!photo && !tip) continue
      const done = h.completions.some((ms) => logicalDayStr(ms) === today)
      next.set(h._id, done)
      const flipped = done && seen.current && seen.current.get(h._id) === false
      if (flipped && !photo && !h.measure?.fast)
        void claimTip({ habitId: h._id })
          .then((r) => {
            if (r)
              setReq({
                habitId: h._id,
                title: h.title,
                category: h.category,
                date: r.date,
                ask: 'sometimes',
                discover: 'tip',
              })
          })
          .catch(() => {})
      else if (flipped && photo)
        void ask({ habitId: h._id })
          .then((r) => {
            if (r)
              setReq({
                habitId: h._id,
                title: h.title,
                category: h.category,
                date: r.date,
                ask: h.photoAsk,
              })
          })
          .catch(() => {})
    }
    seen.current = next
  }, [habits, ask, claimTip, tipSeen])
  return req ? <PhotoCheckinSheet {...req} onClose={() => setReq(null)} /> : null
}
